import { CommonModule, NgClass, NgStyle } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, Signal, ViewChild, ViewEncapsulation, WritableSignal, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from "@angular/material/icon";
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatSidenavModule } from "@angular/material/sidenav";
import { RouterLink } from "@angular/router";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { IconDefinition, faChevronLeft, faChevronRight, faPrint } from '@fortawesome/free-solid-svg-icons';
import { DropDownFilterSettings, DropDownListModule, MultiSelectModule } from "@progress/kendo-angular-dropdowns";
import { BadgeAlign, BadgeModule, BadgePosition, BadgeSize, BadgeThemeColor, SkeletonModule } from '@progress/kendo-angular-indicators';
import { InputsModule, SwitchModule } from "@progress/kendo-angular-inputs";
import { LabelModule } from "@progress/kendo-angular-label";
import { ContextMenuComponent, ContextMenuSelectEvent, MenusModule } from "@progress/kendo-angular-menu";
import { QueryClient } from "@tanstack/angular-query-experimental";
import { Subscription } from 'rxjs';
import { DomChangeDirective } from "../../../shared/directives/dom-change.directive";
import { ActivitiesInfo, PropertiesInfo } from '../../../shared/models/common.models';
import { SaveResult } from "../../../shared/models/result.model";
import { AuthorisationService } from "../../../shared/services/authorisation.service";
import { CommonService } from '../../../shared/services/common.service';
import { ConfigurationService } from "../../../shared/services/configuration.service";
import { ExportPrintService } from '../../../shared/services/export-print.service';
import { ActivitiesComponent } from "../activities/activities.component";
import { ActivityBlockerComponent } from "../activity-blocker/activity-blocker.component";
import { CalendarActivity, Category, PropertyType, Station, StationType, WeekListItem } from '../calendars.models';
import { CalendarsService } from '../calendars.service';
import { CategoryComponent } from '../category/category.component';
import { PropertyComponent } from "../property/property.component";

@Component({
  selector: "cb-calendar",
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.scss'],
  providers: [ExportPrintService],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DomChangeDirective, MatDialogModule, CommonModule, SwitchModule, SkeletonModule, MatButtonModule, MenusModule, MatIconModule, FaIconComponent, InputsModule, LabelModule, DropDownListModule, MatSidenavModule, MultiSelectModule, FormsModule, MatMenuModule, NgStyle, BadgeModule, RouterLink, NgClass]
})
export class CalendarComponent
{
  private queryClient = inject(QueryClient);

  @ViewChild("contextMenu", { static: true }) public contextMenu: ContextMenuComponent;

  public appMenu: MatMenu;
  public badgeThemeColor: BadgeThemeColor = "secondary";
  public badgeAlign: BadgeAlign = { vertical: "top", horizontal: "start" };
  public badgePosition: BadgePosition = 'outside';
  public badgeSize: BadgeSize = 'small';
  public defaultStationGroup: StationType = { Id: 0, StationTypeName: "All", ChangedByUserId: null, Disabled: false, LastUpdated: null, SortOrder: 0 };

  private categoryNewClosedSubscription: Subscription;
  private propertyNewClosedSubscription: Subscription;
  private categoryEditClosedSubscription: Subscription;
  private propertyEditClosedSubscription: Subscription;
  private activityEditClosedSubscription: Subscription;
  private calendarInformationUpdatedSubscription: Subscription;
  private calendarDataArrivedSubscription: Subscription;

  private dialogIsOpen = false;

  private _hideActivityDetails: boolean = false;

  // Added: delegated events + lightweight hover debounce (no RxJS)
  private detachDelegated?: () => void;
  private hoverTimers = new Map<string, any>();
  private propertyHoverTimers = new Map<number, any>();

  get hideActivityDetails(): boolean
  {
    return this._hideActivityDetails;
  }
  set hideActivityDetails(value: boolean)
  {
    this._hideActivityDetails = value;
  }

  public dropdownSettings = {};
  public faChevronLeft: IconDefinition = faChevronLeft;
  public faChevronRight: IconDefinition = faChevronRight;
  public faPrint: IconDefinition = faPrint;

  public configurationService = inject(ConfigurationService);
  public commonService = inject(CommonService);
  public cdr = inject(ChangeDetectorRef);

  constructor(
    public authorisationService: AuthorisationService,
    private exportPrintService: ExportPrintService,
    public calendarsService: CalendarsService,
    private dialog: MatDialog,
    private zone: NgZone)
  {


  }

  public filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains'
  };

  setEventDayMonthYear(day: string, month: number, year: number, stationId: number, stationTypeId: number)
  {
    this.calendarsService.currentEventDay = parseInt(day);
    this.calendarsService.currentEventMonth = month;
    this.calendarsService.currentEventYear = year;
    this.calendarsService.currentEventStationId = stationId;
    this.calendarsService.currentEventStationTypeId = stationTypeId;
  }

  public isPrinting: WritableSignal<boolean> = signal(false);
  public calendarLoadedWithData: WritableSignal<boolean> = signal(false);

  public categories: Signal<Category[]> = computed(() =>
  {
    // Safely default to empty arrays to avoid calling methods on undefined
    const selectedTypeIds: number[] = this.calendarsService.selectedPropertyTypeIds?.() ?? [];
    const allCategories: Category[] = this.calendarsService.categories?.() ?? [];
    const allProperties = this.calendarsService.properties?.() ?? [];

    let _categories: Category[] = [];

    if (!selectedTypeIds || selectedTypeIds.length === 0 || (selectedTypeIds.length === 1 && selectedTypeIds[0] === 0))
    {
      _categories = allCategories;
    }
    else
    {
      const propertyCategoryIds = allProperties
        .filter((p: any) => selectedTypeIds.includes(p?.PropertyType?.Id ?? -1))
        .map((x: any) => x.CategoryId);

      _categories = allCategories.filter((f: Category) => propertyCategoryIds.includes(f.Id));
    }

    // Ensure that there are always categories returned so if the above results in nothing, return all
    if (!_categories || _categories.length === 0)
    {
      _categories = allCategories;
    }

    return _categories ?? [];
  });

  public stationGroups: Signal<StationType[]> = computed(() =>
  {
    // Safely default to empty arrays to avoid calling methods on undefined
    const selectedTypeIds: number[] = this.calendarsService.selectedPropertyTypeIds?.() ?? [];
    const allStationGroups: StationType[] = this.calendarsService.stationGroups?.() ?? [];

    let _stationGroups: StationType[] = [];

    if (!selectedTypeIds || selectedTypeIds.length === 0 || (selectedTypeIds.length === 1 && selectedTypeIds[0] === 0))
    {
      _stationGroups = allStationGroups;
    }
    else
    {
      const propertyTypes: PropertyType[] = (this.calendarsService.propertyTypes?.() ?? [])
        .filter((f: PropertyType) => selectedTypeIds.includes(f.Id));

      const availStations: Station[] = (this.calendarsService.stations?.() ?? [])
        .filter((f: Station) => (f.PlatformType?.Name === "Internet" && propertyTypes.some(s => !s.IsBroadcast)) ||
          (f.PlatformType?.Name === "Radio Station" && propertyTypes.some(s => s.IsBroadcast)));

      _stationGroups = (this.calendarsService.stationGroups?.() ?? [])
        .filter((f: StationType) => availStations.map((s: Station) => s.StationTypeId).includes(f.Id));
    }

    // Ensure that there are always stationGroups returned so if the above results in nothing, return all
    if (!_stationGroups || _stationGroups.length === 0)
    {
      _stationGroups = allStationGroups;
    }

    return _stationGroups ?? [];
  });

  public hasSelectedStations: Signal<boolean> = computed(() =>
  {
    return this.calendarsService.selectedStations != null && this.calendarsService.selectedStations.length > 0;
  });



  ngOnInit(): void
  {
    window.my = window.my || {};
    window.my.cc = this.pCategoryClick.bind(this);
    window.my.pc = this.pPropertyClick.bind(this);
    window.my.ph = this.pPropertyHover.bind(this);
    window.my.ac = this.pActivityClick.bind(this);
    window.my.ah = this.pActivityHover.bind(this);
    window.my.arc = this.pRightActivityClick.bind(this);

    if (this.authorisationService.currentuser != null
      && this.authorisationService.currentuser.Id > 0
      && !this.calendarsService.homeStation && this.calendarsService.stations()

      // Only run legacy default-station logic if none is already selected
      //&& (!this.calendarsService.selectedStations || this.calendarsService.selectedStations.length === 0)
    )
    {
      // this.calendarsService.homeStation = this.calendarsService.stations().filter((s:Station) => s.Id == this.authorisationService.currentuser.HomeStationId)[0];
      const homestation: Station = this.calendarsService.stations().filter((s: Station) => s.Id == this.authorisationService.currentuser.HomeStationId)[0];
      this.calendarsService.homeStation = homestation;

      //if (!this.calendarsService.setStationsFromStorage())
      //{
      //  this.calendarsService.noOfWeeks = 5;

      //  this.calendarsService.resetSelectedStations();

      //  const newSelectedStations: Station[] = [];

      //  if (homestation)
      //  {
      //    //Select user's home station.
      //    this.calendarsService.selectedStationGroupId = homestation.StationType.Id;
      //    newSelectedStations.push(homestation);

      //    this.calendarsService.homeStation = homestation;
      //  }
      //  else
      //  {
      //    //Select 1st station in list if home station not found.
      //    this.calendarsService.selectedStationGroupId = this.calendarsService.stations()[0].StationType.Id;
      //    newSelectedStations.push(this.calendarsService.stations()[0]);
      //  }

      //  //selected Stations set NOT added to with push so selectedStations localStorage saved
      //  this.calendarsService.selectedStations = newSelectedStations;
      //}

    }

    this.calendarDataArrivedSubscription = this.calendarsService.calendarDataArrived.subscribe((hasData: boolean) =>
    {
      this.calendarLoadedWithData.set(hasData);
    });

    this.calendarInformationUpdatedSubscription = this.calendarsService.calendarInformationUpdated.subscribe((result: boolean) =>
    {
      if (result)
      {
        this.cdr.markForCheck();
      }
    });
  }


  ngAfterViewInit()
  {
    this.calendarsService.resetCalendarData();

    // Wire delegated events so server HTML always responds (click/hover/context menu)
    this.wireDelegatedDomEvents();

    this.cdr.detectChanges();
  }


  // Robust delegated event wiring (click, contextmenu, mouseover) without template changes
  private wireDelegatedDomEvents()
  {
    const host = document.getElementById('core-calendar');
    if (!host) return;

    const getActivityId = (el: HTMLElement): string | null =>
    {
      if (el.getAttribute('a') || el.getAttribute('data-a')) return el.id || null;
      const hidden = el.querySelector('input[type=hidden][data-activity-id]') as HTMLElement | null;
      if (hidden) return hidden.getAttribute('data-activity-id');
      return el.id || null;
    };

    const closest = (ev: Event, sel: string) =>
      (ev.target as HTMLElement)?.closest(sel) as HTMLElement | null;

    const clickHandler = (ev: MouseEvent) =>
    {
      const el = closest(ev, "[a],[data-a],[data-property-id],[data-category-id]");
      if (!el) return;
      if (el.hasAttribute("a") || el.hasAttribute("data-a"))
      {
        const id = getActivityId(el);
        if (id) this.pActivityClick(ev, id);
        return;
      }
      const propAttr = el.getAttribute('data-property-id');
      if (propAttr) { const id = +propAttr; if (id > 0) this.pPropertyClick(ev, id); return; }
      const catAttr = el.getAttribute('data-category-id');
      if (catAttr) { const id = +catAttr; if (id > 0) this.pCategoryClick(ev, id); }
    };

    // CHANGED: prevent browser menu and bubbling; then route to existing handler
    const contextHandler = (ev: MouseEvent) =>
    {
      const el = closest(ev, "[a],[data-a]");
      if (!el) return;
      ev.preventDefault();
      ev.stopPropagation();
      const id = getActivityId(el);
      if (id) this.pActivityClick(ev, id);
    };

    const hoverHandler = (ev: MouseEvent) =>
    {
      const el = closest(ev, "[a],[data-a],[data-property-id]");
      if (!el) return;
      if (el.hasAttribute("a") || el.hasAttribute("data-a"))
      {
        const id = getActivityId(el);
        if (id) this.pActivityHover(ev, id);
        return;
      }
      const propAttr = el.getAttribute('data-property-id');
      if (propAttr) { const id = +propAttr; if (id > 0) this.pPropertyHover(ev, id); }
    };

    host.addEventListener('click', clickHandler);
    host.addEventListener('contextmenu', contextHandler);
    host.addEventListener('mouseover', hoverHandler);

    this.detachDelegated = () =>
    {
      host.removeEventListener('click', clickHandler);
      host.removeEventListener('contextmenu', contextHandler);
      host.removeEventListener('mouseover', hoverHandler);
    };
  }


  calendarScrolled(event: any)
  {
    if (event)
    {
      if (event.stopPropagation)
      {
        // stop event bubbling up
        event.stopPropagation();
      }

      //prevents browser from performing the default action for this element
      event.preventDefault();

      if (event.target && event.target.scrollTop !== undefined)
      {
        localStorage.setItem("calendarScroll", String(event.target.scrollTop));
      }
    }

  }

  onDomChange(event: any): void
  {
    if (event.removedNodes.length != event.addedNodes.length || JSON.stringify(event.removedNodes) != JSON.stringify(event.addedNodes))
    {
      this.cdr.detectChanges();
    }
  }

  print(event: any)
  {
    try
    {
      if (event)
      {
        if (event.stopPropagation)
        {
          // stop event bubbling up
          event.stopPropagation();
        }

        //prevents browser from performing the default action for this element
        event.preventDefault();
      }

      this.isPrinting.set(true);
      this.commonService.isLoading.set(true);
      //this.cdr.detectChanges();

      const reportTitle = "Audio Calendar";

      const calendarElement: any = document.getElementById("core-calendar");

      this.exportPrintService.exportElement(calendarElement, reportTitle.replace(/\s/g, '') + ".pdf", this.commonService.reportNewTab).then((data: any) =>
      {
        this.isPrinting.set(false);
        this.commonService.isLoading.set(false);
        //this.cdr.detectChanges();
      })
        .catch((err: any) =>
        {
          this.isPrinting.set(false);
          this.commonService.isLoading.set(false);
          //this.cdr.detectChanges();
        });
    }
    catch (e)
    {
      this.isPrinting.set(false);
      this.commonService.isLoading.set(false);
      // this.cdr.detectChanges();
    }
  }



  next(event: any)
  {
    this.nextWeek();
  }

  nextWeek()
  {
    const nextWeek: WeekListItem = this.calendarsService.weeksForSelectedYear.filter(w => w.WeekNo == this.calendarsService.selectedWeek.WeekNo + 1)[0];

    if (nextWeek)
    {
      this.calendarsService.selectedWeek = nextWeek;

      this.calendarsService.resetCalendarData();
    }
    else
    {
      const lastSelectableYear: number = this.calendarsService.years[this.calendarsService.years.length - 1];
      const nextYear: number = this.calendarsService.selectedYear + 1;

      if (nextYear <= lastSelectableYear)
      {
        this.calendarsService.selectedYear = nextYear;

        const firstWeek: WeekListItem = this.calendarsService.weeksForSelectedYear.filter(w => w.WeekNo == 1)[0];

        if (firstWeek)
        {
          this.calendarsService.selectedWeek = firstWeek;

          setTimeout(() =>
          {
            this.calendarsService.resetCalendarData();
          }, 0);
        }
      }
    }
  }


  previous(event: any)
  {
    this.previousWeek();
  }


  previousWeek()
  {
    const previousWeek: WeekListItem = this.calendarsService.weeksForSelectedYear.filter(w => w.WeekNo == this.calendarsService.selectedWeek.WeekNo - 1)[0];

    if (previousWeek)
    {
      this.calendarsService.selectedWeek = previousWeek;

      this.calendarsService.resetCalendarData();
    }
    else
    {
      const firstSelectableYear: number = this.calendarsService.years[0];
      const previousYear: number = this.calendarsService.selectedYear - 1;

      if (previousYear >= firstSelectableYear)
      {
        this.calendarsService.selectedYear = previousYear;

        const lastWeekInPreviousYear: number = this.calendarsService.weeksForSelectedYear[this.calendarsService.weeksForSelectedYear.length - 1].WeekNo;

        const lastWeek: WeekListItem = this.calendarsService.weeksForSelectedYear.filter(w => w.WeekNo == lastWeekInPreviousYear)[0];

        if (lastWeek)
        {
          this.calendarsService.selectedWeek = lastWeek;

          setTimeout(() =>
          {
            this.calendarsService.resetCalendarData();
          }, 0);
        }
      }
    }
  }

  propertyHover(event: any, id: number)
  {
    if (event)
    {
      if (event.stopPropagation)
      {
        // stop event bubbling up 
        event.stopPropagation();
      }

      //prevents browser from performing the default action for this element
      event.preventDefault();
    }

    if (id > 0)
    {
      this.queryClient.prefetchQuery({
        queryKey: ["Property", id],
        queryFn: () => this.calendarsService.getProperty(id),
        //staleTime: this.configurationService.cbSettings().prefetchQueryStaleTime
      });
    }
  }

  activityHover(event: any, id: string)
  {
    const hid = id || (event?.currentTarget as HTMLElement)?.id || "";
    const payload = this.readEmbeddedPayloadById(hid);
    const activity: CalendarActivity = this.getCalendarActivityFromPayload(payload);

    if (!activity.PropertyId || !activity.StationId || activity.Level == null)
    {
      const hk = this.parseHiddenKey(hid);
      if (hk.propertyId && !activity.PropertyId) activity.PropertyId = hk.propertyId;
      if (hk.stationId && !activity.StationId) activity.StationId = hk.stationId;
      if (hk.level != null && (activity.Level == null || activity.Level === 0)) activity.Level = hk.level;
    }

    if (event)
    {
      if (event.stopPropagation)
      {
        // stop event bubbling up 
        event.stopPropagation();
      }

      //prevents browser from performing the default action for this element
      event.preventDefault();
    }


    const stationIds: number[] = [];
    this.calendarsService.selectedStations.map((s) => stationIds.push(s.Id));

    const activitiesParameters = {
      startDate: this.commonService.dateToWebApiString(activity.FirstDateInCalendarRange),
      endDate: this.commonService.dateToWebApiString(activity.LastDateInCalendarRange),
      stationIds: stationIds,
      propertyId: activity.PropertyId,
      level: activity.Level
    };

    if (this.configurationService.cbSettings().calendarServiceUrl.length > 0 && activitiesParameters?.stationIds?.length > 0)
    {
      this.queryClient.prefetchQuery({
        queryKey: ["Activities", activitiesParameters],
        queryFn: () => this.calendarsService.getActivitiesForPropertyAndDateRange(activitiesParameters),
        //staleTime: this.configurationService.cbSettings().prefetchQueryStaleTime
      });
    }

    if (activity?.Id > 0)
    {
      this.queryClient.prefetchQuery({
        queryKey: ["Activity", activity.Id],
        queryFn: () => this.calendarsService.getActivity(activity.Id),
        //staleTime: this.configurationService.cbSettings().prefetchQueryStaleTime
      });
    }

    return true;
  }


  // Debounced hover entry points using per-id timers (no RxJS)
  pActivityHover(event: any, id: string)
  {
    const key = id || (event?.currentTarget as HTMLElement)?.id || "";
    const ms = this.configurationService.cbSettings().cacheDebounceTime;
    // Clear previous
    const prev = this.hoverTimers.get(key);
    if (prev) clearTimeout(prev);
    // Schedule new
    const t: any = window.setTimeout(() =>
    {
      this.zone.run(() => this.activityHover(event, key));
    }, ms);
    this.hoverTimers.set(key, t);
  }

  pPropertyHover(event: any, id: number)
  {
    const key = id ?? 0;
    const ms = this.configurationService.cbSettings().cacheDebounceTime;
    const prev = this.propertyHoverTimers.get(key);
    if (prev) clearTimeout(prev);
    const t: any = window.setTimeout(() =>
    {
      this.zone.run(() => this.propertyHover(event, key));
    }, ms);
    this.propertyHoverTimers.set(key, t);
  }

  pCategoryClick(event: any, id: number)
  {
    this.zone.run(() => this.categoryClick(event, id));
  }

  pPropertyClick(event: any, id: number)
  {
    this.zone.run(() => this.propertyClick(event, id));
  }

  pActivityClick(event: any, id: string)
  {
    this.zone.run(() => this.activityClick(event, id));
  }

  pRightActivityClick(event: any)
  {
    this.zone.run(() => this.activityRightClick(event));
  }

  categoryClick(event: any, id: number)
  {
    if (event)
    {
      if (event.stopPropagation)
      {
        // stop event bubbling up
        event.stopPropagation();
      }

      //prevents browser from performing the default action for this element
      event.preventDefault();
    }

    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.EditCategory) || this.authorisationService.hasPermission(this.authorisationService.Permissions.ViewCategory))
    {
      if (!this.dialogIsOpen)
      {
        try
        {
          this.dialogIsOpen = true;

          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = "24rem";
          dialogConfig.hasBackdrop = true;

          dialogConfig.data = id;

          try
          {
            const dialogRef = this.dialog.open(CategoryComponent, dialogConfig);

            this.categoryEditClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
            {
              this.dialogIsOpen = false;

              if (result && result.IsSuccessful)
              {
                this.calendarsService.resetHttpCache();
                this.calendarsService.resetCalendarData();
                this.calendarsService.dataResetFromCategorySave = true;
              }
            });
          }
          catch (e)
          {
            this.dialogIsOpen = false;
          }
        }
        catch (e)
        {
          this.dialogIsOpen = false;
        }
      }
    }

  }

  propertyClick(event: any, id: number)
  {
    if (event)
    {
      if (event.stopPropagation)
      {
        // stop event bubbling up
        event.stopPropagation();
      }

      //prevents browser from performing the default action for this element
      event.preventDefault();
    }

    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.EditProperty) || this.authorisationService.hasPermission(this.authorisationService.Permissions.ViewProperty))
    {
      if (!this.dialogIsOpen)
      {
        try
        {
          this.dialogIsOpen = true;

          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = "52rem";
          dialogConfig.height = "98vh";
          dialogConfig.hasBackdrop = true;

          const propertiesInfo: PropertiesInfo = new PropertiesInfo();
          propertiesInfo.id = id;
          propertiesInfo.isOnline = false;

          dialogConfig.data = propertiesInfo;

          const dialogRef = this.dialog.open(PropertyComponent, dialogConfig);

          this.propertyEditClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
          {
            this.dialogIsOpen = false;

            if (result && result.IsSuccessful)
            {
              this.calendarsService.resetHttpCache();
              this.calendarsService.resetCalendarData();
              this.calendarsService.dataResetFromPropertySave = true;
            }
          });
        }
        catch (e)
        {
          this.dialogIsOpen = false;
        }
      }
    }
  }

  activityRightClick(event: any)
  {
    if (event)
    {
      if (event.stopPropagation)
      {
        // stop event bubbling up
        event.stopPropagation();
      }

      //prevents browser from performing the default action for this element
      event.preventDefault();
    }
  }

  openNewActivity(activity: CalendarActivity, currentActivityOnSpot: boolean)
  {
    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.AddBroadcastActivity)
      || this.authorisationService.hasPermission(this.authorisationService.Permissions.AddNonBroadcastActivity))
    {
      if (!this.dialogIsOpen)
      {
        this.dialogIsOpen = true;

        if (activity.IsAvailable)
        {
          const stationIds: number[] = [];

          //Only want activities for station of current activity clicked
          stationIds.push(activity.StationId);

          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = "95%";
          dialogConfig.maxWidth = "1280px";
          dialogConfig.hasBackdrop = true;

          let dialogRef: any = null;

          activity.Id = 0;

          const actInfo: ActivitiesInfo = new ActivitiesInfo();
          actInfo.activity = activity;
          actInfo.stationIds = stationIds;
          actInfo.newActivityOnOccupiedSpot = currentActivityOnSpot;

          dialogConfig.data = actInfo;

          dialogRef = this.dialog.open(ActivitiesComponent, dialogConfig);

          this.activityEditClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
          {
            this.dialogIsOpen = false;

            if (result && result.IsSuccessful)
            {
              this.calendarsService.resetHttpCache();
              this.calendarsService.resetCalendarData();
              this.calendarsService.dataResetFromActivitySave = true;
            }
          });
        }
      }
    }
  }

  public onSelect(e: ContextMenuSelectEvent): void
  {
    // Prefer the element that carries the packed payload
    const host = (e.target as HTMLElement)?.closest("[a],[data-a]") as HTMLElement | null;
    const hid = host?.id || "";
    const payload = this.readEmbeddedPayloadById(hid);
    const activity: CalendarActivity = this.getCalendarActivityFromPayload(payload);

    if (!activity.PropertyId || !activity.StationId || activity.Level == null)
    {
      const hk = this.parseHiddenKey(hid);
      if (hk.propertyId && !activity.PropertyId) activity.PropertyId = hk.propertyId;
      if (hk.stationId && !activity.StationId) activity.StationId = hk.stationId;
      if (hk.level != null && (activity.Level == null || activity.Level === 0)) activity.Level = hk.level;
    }

    this.calendarsService.stationIdOfClickedActivity = activity.StationId;

    const currentActivityOnSpot: boolean = activity && activity.Id > 0;

    activity.StartDate = activity.FirstDateInCalendarRange;
    activity.EndDate = activity.LastDateInCalendarRange;
    activity.Id = 0;

    if (activity.Id < 1 && new Date(activity.EndDate) < this.commonService.today)
    {
      this.dialogIsOpen = false;
      return;
    }

    switch (e.item.text)
    {
      case "New Activity":
        this.calendarsService.prefetchProperty(activity?.PropertyId);
        this.openNewActivity(activity, currentActivityOnSpot);
        break;
      case "New Blocker":
        this.openNewActivityBlocker(activity);
        break;
    }
  }

  openNewActivityBlocker(activity: CalendarActivity)
  {
    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.AddActivityBlocker))
    {
      if (!this.dialogIsOpen)
      {
        this.dialogIsOpen = true;

        if (activity.IsAvailable)
        {
          const stationIds: number[] = [];

          //Only want activities for station of current activity clicked
          stationIds.push(activity.StationId);

          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = "95%";
          dialogConfig.maxWidth = "55rem";
          dialogConfig.hasBackdrop = true;

          let dialogRef: any = null;

          activity.IsGuaranteed = false;
          activity.IsPending = false;
          activity.OnHold = false;
          activity.IsUnConfirmedSold = false;
          activity.IsSold = true;

          dialogConfig.data = activity;

          dialogRef = this.dialog.open(ActivityBlockerComponent, dialogConfig);

          this.activityEditClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
          {
            this.dialogIsOpen = false;

            if (result && result.IsSuccessful)
            {
              this.calendarsService.resetHttpCache();
              this.calendarsService.resetCalendarData();
              this.calendarsService.dataResetFromActivitySave = true;
            }
          });
        }
      }
    }
  }

  private readEmbeddedPayloadById(id: string): string
  {
    const host = document.getElementById(id) as HTMLElement | null;
    if (host)
    {
      // shortest first
      const v = host.getAttribute("a") || host.getAttribute("data-a");
      if (v) return v;
    }
    // legacy fallbacks if any remain
    const el = document.getElementById(id) as HTMLInputElement | HTMLScriptElement | null;
    if (el)
    {
      if ((el as HTMLInputElement).value !== undefined) return (el as HTMLInputElement).value || "";
      return (el.textContent || "").trim();
    }
    return "";
  }

  private parseHiddenKey(id: string): { propertyId?: number; level?: number; stationId?: number }
  {
    // New compact (base36, day-of-year base36): {P36}m{DOY36}l{V36}s{S36}
    let m = /^([0-9A-Z]+)m([0-9A-Z]+)l([0-9A-Z]+)s([0-9A-Z]+)$/.exec(id);
    if (m)
    {
      const p = parseInt(m[1], 36);
      const v = parseInt(m[3], 36);
      const s = parseInt(m[4], 36);
      return { propertyId: p, level: v, stationId: s };
    }

    // Legacy decimal: {propId}m{month}d{day}l{level}s{stationId}
    m = /^(\d+)m(\d+)d(\d+)l(\d+)s(\d+)$/.exec(id);
    if (m)
    {
      return { propertyId: +m[1], level: +m[4], stationId: +m[5] };
    }

    return {};
  }

  getCalendarActivityFromPayload(payload: string)
  {
    const s = (payload ?? "").trim();

    if (s.startsWith("{"))
    {
      // legacy JSON unchanged
      const o: any = JSON.parse(s);
      const ca: CalendarActivity = new CalendarActivity();
      ca.Id = o.I ?? 0;
      ca.IsAvailable = (o.A ?? 1) === 1;
      ca.IsBlocker = (o.B ?? 0) === 1;
      const dVal = o.D;
      ca.IsSold = dVal === undefined || dVal === null || dVal === 1;
      ca.ClientName = o.C ?? null;
      ca.PropertyId = o.P ?? 0;
      ca.StationId = o.S ?? 0;
      ca.Level = o.V ?? 0;
      ca.FirstDateInCalendarRange = this.commonService.adjustToUtcDateOnly(o.F);
      if (ca.Id > 0)
      {
        ca.LastDateInCalendarRange = this.commonService.adjustToUtcDateOnly(o.L);
        ca.EndDate = this.commonService.adjustToUtcDateOnly(o.E);
        ca.StartDate = this.commonService.adjustToUtcDateOnly(o.T);
      } else
      {
        ca.LastDateInCalendarRange = ca.FirstDateInCalendarRange;
        ca.EndDate = ca.FirstDateInCalendarRange;
        ca.StartDate = ca.FirstDateInCalendarRange;
      }
      return ca;
    }

    // handle packed formats (v1 and v2)
    const parts = s.split("|");
    const b36 = (v?: string) => (v == null ? 0 : parseInt(v.toUpperCase().replace(/[^0-9A-Z]/g, ""), 36));
    const epoch2000 = Date.UTC(2000, 0, 1);
    const dayToDate = (d?: string) =>
    {
      if (d == null) return null as any;
      const days = b36(d);
      const dt = new Date(epoch2000 + days * 86400000);
      const iso = `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
      return this.commonService.adjustToUtcDateOnly(iso);
    };

    const ca: CalendarActivity = new CalendarActivity();

    if (parts.length >= 5 && parts.length <= 6)
    {
      // v2 existing: I|F|L|E|T[|flags]
      ca.Id = b36(parts[0]);
      ca.FirstDateInCalendarRange = dayToDate(parts[1]);
      ca.LastDateInCalendarRange = dayToDate(parts[2]);
      ca.EndDate = dayToDate(parts[3]);
      ca.StartDate = dayToDate(parts[4]);
      let flags = parts.length > 5 ? b36(parts[5]) : 0;
      ca.IsSold = (flags & 1) === 0;
      (ca as any).IsFreeOfCharge = (flags & 2) !== 0;
      ca.IsBlocker = (flags & 4) !== 0;
      ca.IsAvailable = true;
      return ca;
    }

    if (parts.length >= 2 && parts.length <= 3)
    {
      // v2 new: I|F[|flags]
      ca.Id = b36(parts[0]);
      ca.FirstDateInCalendarRange = dayToDate(parts[1]);
      ca.LastDateInCalendarRange = ca.FirstDateInCalendarRange;
      ca.EndDate = ca.FirstDateInCalendarRange;
      ca.StartDate = ca.FirstDateInCalendarRange;
      let flags = parts.length > 2 ? b36(parts[2]) : 0;
      ca.IsSold = (flags & 1) === 0;
      (ca as any).IsFreeOfCharge = (flags & 2) !== 0;
      ca.IsBlocker = (flags & 4) !== 0;
      ca.IsAvailable = true;
      return ca;
    }

    // v1 fallback: I|A|B|P|S|V|F|L|E|T[|flags]
    ca.Id = b36(parts[0]);
    ca.IsAvailable = (parts[1] ?? "1") === "1";
    ca.IsBlocker = (parts[2] ?? "0") === "1";
    ca.PropertyId = b36(parts[3]);
    ca.StationId = b36(parts[4]);
    ca.Level = b36(parts[5] ?? "0");
    ca.FirstDateInCalendarRange = dayToDate(parts[6]);
    if (ca.Id > 0)
    {
      ca.LastDateInCalendarRange = dayToDate(parts[7]);
      ca.EndDate = dayToDate(parts[8]);
      ca.StartDate = dayToDate(parts[9]);
    } else
    {
      ca.LastDateInCalendarRange = ca.FirstDateInCalendarRange;
      ca.EndDate = ca.FirstDateInCalendarRange;
      ca.StartDate = ca.FirstDateInCalendarRange;
    }
    let flags = parts.length > 10 ? b36(parts[10]) : 0;
    ca.IsSold = (flags & 1) === 0 || parts.length <= 10; // older v1 often omitted flags
    (ca as any).IsFreeOfCharge = (flags & 2) !== 0;
    return ca;
  }

  activityClick(event: any, id: string)
  {
    const hid = id || (event?.currentTarget as HTMLElement)?.id || "";
    const payload = this.readEmbeddedPayloadById(hid);
    const activity: CalendarActivity = this.getCalendarActivityFromPayload(payload);

    if (!activity.PropertyId || !activity.StationId || activity.Level == null)
    {
      const hk = this.parseHiddenKey(hid);
      if (hk.propertyId && !activity.PropertyId) activity.PropertyId = hk.propertyId;
      if (hk.stationId && !activity.StationId) activity.StationId = hk.stationId;
      if (hk.level != null && (activity.Level == null || activity.Level === 0)) activity.Level = hk.level;
    }

    if (!activity)
    {
      this.dialogIsOpen = false;
      return;
    }

    // Handle right-click or Alt-key distinctly to avoid short-circuiting the logic below.
    if (event?.button === 2 || event?.altKey)
    {
      event.preventDefault?.();
      event.stopPropagation?.();

      if (activity.Id < 1)
      {
        // Empty slot → show context menu, allow "New Activity"/"New Blocker"
        const host = document.getElementById(hid);
        if (host) this.contextMenu.show(host);
      }
      else
      {
        // Existing activity → only allow creating a new activity on top if NOT sold and NOT a blocker
        if (!activity.IsSold && !activity.IsBlocker)
        {
          activity.StartDate = activity.FirstDateInCalendarRange;
          activity.EndDate = activity.LastDateInCalendarRange;
          activity.Id = 0;
          this.openNewActivity(activity, true);
        }
        // If sold or blocker, do nothing (no popup/menu)
      }
      return;
    }

    // Left-click flow
    if (event)
    {
      if (event.stopPropagation) event.stopPropagation();
      if (event.cancelBubble) event.cancelBubble = true;
      event.preventDefault?.();
    }

    this.calendarsService.prefetchProperty(activity?.PropertyId);
    this.calendarsService.stationIdOfClickedActivity = activity.StationId;

    const startDate = new Date(activity.StartDate);

    // Don't allow creation of new activities for past dates
    if (activity.Id < 1 && startDate < this.commonService.today)
    {
      this.dialogIsOpen = false;
      return;
    }

    // New activity (left-click) - Ctrl creates blocker
    if (activity.Id < 1)
    {
      if (event?.ctrlKey)
      {
        this.openNewActivityBlocker(activity);
        return;
      }
    }

    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.ViewActivityImplementation)
      || this.authorisationService.hasPermission(this.authorisationService.Permissions.ViewActivitySales)
      || this.authorisationService.hasPermission(this.authorisationService.Permissions.ViewActivityStationSpecific)
      || (activity.Id < 1 && this.authorisationService.hasPermission(this.authorisationService.Permissions.AddBroadcastActivity))
      || (activity.Id > 0 && (this.authorisationService.hasPermission(this.authorisationService.Permissions.EditActivitySales)
        || this.authorisationService.hasPermission(this.authorisationService.Permissions.EditActivityImplementation)
        || this.authorisationService.hasPermission(this.authorisationService.Permissions.EditActivityStationSpecific))))
    {
      if (!this.dialogIsOpen && activity.IsAvailable)
      {
        this.dialogIsOpen = true;

        const stationIds: number[] = [activity.StationId];

        const dialogConfig = new MatDialogConfig();
        let compmonent: any;
        let width: any = "95%";
        let maxWidth: any = "1280px";
        let data: any;

        if (activity.IsBlocker)
        {
          compmonent = ActivityBlockerComponent;
          width = "75%";
          maxWidth = "900px";
          data = activity;
        }
        else
        {
          compmonent = ActivitiesComponent;
          data = new ActivitiesInfo();
          data.activity = activity;
          data.stationIds = stationIds;
          data.newActivityOnOccupiedSpot = false;
        }

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.width = width;
        dialogConfig.maxWidth = maxWidth;
        dialogConfig.hasBackdrop = true;
        dialogConfig.data = data;

        const dialogRef = this.dialog.open(compmonent, dialogConfig);

        this.activityEditClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
        {
          this.dialogIsOpen = false;

          if (result && result.IsSuccessful)
          {
            this.calendarsService.resetHttpCache();
            this.calendarsService.resetCalendarData();
            this.calendarsService.dataResetFromActivitySave = true;
          }
        });
      }
    }
  }

  newCategory()
  {
    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.AddCategory))
    {
      if (!this.dialogIsOpen)
      {
        try
        {
          this.dialogIsOpen = true;

          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = "24rem";
          dialogConfig.hasBackdrop = true;
          dialogConfig.data = 0;

          const dialogRef = this.dialog.open(CategoryComponent, dialogConfig);

          this.categoryNewClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
          {
            this.dialogIsOpen = false;

            if (result && result.IsSuccessful)
            {
              this.calendarsService.resetHttpCache();
              this.calendarsService.resetCalendarData();

              this.calendarsService.dataResetFromCategorySave = true;
            }
          });
        }
        catch (e)
        {
          this.dialogIsOpen = false;
        }
      }
    }
  }

  newProperty()
  {
    if (this.authorisationService.hasPermission(this.authorisationService.Permissions.AddProperty))
    {
      if (!this.dialogIsOpen)
      {
        try
        {
          this.dialogIsOpen = true;

          const dialogConfig = new MatDialogConfig();

          dialogConfig.disableClose = true;
          dialogConfig.autoFocus = true;
          dialogConfig.width = "52rem";
          dialogConfig.height = "98vh";
          dialogConfig.hasBackdrop = true;

          const propertiesInfo: PropertiesInfo = new PropertiesInfo();
          propertiesInfo.id = 0;
          propertiesInfo.isOnline = false;

          dialogConfig.data = propertiesInfo;

          const dialogRef = this.dialog.open(PropertyComponent, dialogConfig);

          this.propertyNewClosedSubscription = dialogRef.afterClosed().subscribe((result: SaveResult) =>
          {
            this.dialogIsOpen = false;

            if (result && result.IsSuccessful)
            {
              this.calendarsService.resetHttpCache();
              this.calendarsService.resetCalendarData();

              this.calendarsService.dataResetFromPropertySave = true;
            }
          });
        }
        catch (e)
        {
          this.dialogIsOpen = false;
        }
      }
    }
  }


  swipeLeft(event: any)
  {
    if (event)
    {
      //prevents browser from performing the default action for this element
      event.preventDefault();
    }

    this.nextWeek();
  }

  swipeRight(event: any)
  {
    if (event)
    {
      //prevents browser from performing the default action for this element
      event.preventDefault();
    }

    this.previousWeek();
  }

  pinchIn(event: any)
  {
    if (event)
    {
      //prevents browser from performing the default action for this element
      event.preventDefault();

      const element: any = event.target;

      let scale: any = element.css('transform');

      scale = (scale == null ? element.css('-webkit-transform') : scale);
      scale = (scale == null ? element.css('-ms-transform') : scale);
      scale = scale.split(" ");
      scale = parseFloat(scale[0].substring(7, scale[0].length - 1));

      if (scale > 1)
      {
        scale = ('scale(' + (scale - .1).toString() + ')');
        element.css({ 'transform': scale, '-ms-transform': scale, '-webkit-transform': scale });
      }
    }
  }

  pinchOut(event: any)
  {
    if (event)
    {
      //prevents browser from performing the default action for this element
      event.preventDefault();

      const element: any = event.target;

      let scale: any = element.css('transform');

      scale = (scale == null ? element.css('-webkit-transform') : scale);
      scale = (scale == null ? element.css('-ms-transform') : scale);
      scale = scale.split(" ");
      scale = parseFloat(scale[0].substring(7, scale[0].length - 1));

      if (scale < 5)
      {
        scale = ('scale(' + (scale + .1).toString() + ')');

        element.css({ 'transform': scale, '-ms-transform': scale, '-webkit-transform': scale });
      }
    }
  }

  handleWeeksChange(week: WeekListItem)
  {
    if (week)
    {
      this.calendarsService.resetCalendarData();
    }
  }

  handleNoOfWeeksChange(event: any)
  {
    this.calendarsService.noOfWeeks = parseInt(event.target.value);

    this.calendarsService.resetCalendarData();
  }

  handleYearChange(event: any)
  {
    this.calendarsService.selectedYear = event;

    let weekInNewYear: WeekListItem = this.calendarsService.weeksForSelectedYear.filter(w => w.WeekNo == this.calendarsService.selectedWeek.WeekNo)[0];

    if (!weekInNewYear)
    {
      weekInNewYear = this.calendarsService.weeksForSelectedYear.filter(w => w.WeekNo == 1)[0];
    }

    this.calendarsService.selectedWeek = weekInNewYear;

    this.calendarsService.resetCalendarData();
  }

  handleCategoryChange(categoryIds: any)
  {
    this.calendarsService.selectedCategoryIds = categoryIds;
  }

  handleAvailOnlyChange(availOnly: any)
  {
    this.filter();
  }

  handlePropertyTypeChange(propertyType: any)
  {
    // Ensure a new array instance to trigger Angular Signals effect reliably
    this.calendarsService.selectedPropertyTypeIds.set([...(propertyType ?? [])]);

    this.calendarsService.selectedCategoryIds = this.categories().map((m: Category) => m.Id).filter((i: any) => this.calendarsService.selectedCategoryIds.includes(i));

    this.resetStationGroup(0);

    // Persist immediately to avoid relying solely on the effect in case the UI reuses the same array instance later
    localStorage.setItem("selectedPropertyTypeIds", JSON.stringify(this.calendarsService.selectedPropertyTypeIds()));
  }

  resetStationGroup(stationGroupId: number)
  {
    this.calendarsService.selectedStationGroupId = stationGroupId;

    if (!this.calendarsService.selectedStations.map(s => s.Id).some(s => this.calendarsService.availableStations.map(s => s.Id).includes(s)))
    {
      this.calendarsService.selectedStations = [];
    }

    this.calendarsService.resetSelectedStations();

    this.calendarsService.setNoOfWeeks();
  }

  handleStationGroupChange(stationGroupId: any)
  {
    this.resetStationGroup(stationGroupId);
  }

  stationsSelectedChange(stations: any)
  {
    this.calendarsService.setNoOfWeeks();
  }

  async filter()
  {
    this.calendarsService.resetCalendarData();
  }

  drawerClosed()
  {
    this.filter();
  }

  // Add a helper method to toggle the availableOnly flag when clicking the adjacent labels
  public setAvailableOnly(value: boolean)
  {
    if (this.commonService.isLoading())
    {
      return;
    }

    if (this.calendarsService.availableOnly !== value)
    {
      this.calendarsService.availableOnly = value;
      this.handleAvailOnlyChange(value);
    }
  }
  ngOnDestroy()
  {
    // remove delegated listeners
    if (this.detachDelegated) this.detachDelegated();

    // clear hover timers
    this.hoverTimers.forEach(t => clearTimeout(t));
    this.propertyHoverTimers.forEach(t => clearTimeout(t));
    this.hoverTimers.clear();
    this.propertyHoverTimers.clear();

    // prevent memory leak when component destroyed
    if (this.categoryEditClosedSubscription)
    {
      this.categoryEditClosedSubscription.unsubscribe();
    }
    if (this.categoryNewClosedSubscription)
    {
      this.categoryNewClosedSubscription.unsubscribe();
    }
    if (this.propertyEditClosedSubscription)
    {
      this.propertyEditClosedSubscription.unsubscribe();
    }
    if (this.propertyNewClosedSubscription)
    {
      this.propertyNewClosedSubscription.unsubscribe();
    }
    if (this.activityEditClosedSubscription)
    {
      this.activityEditClosedSubscription.unsubscribe();
    }
    if (this.calendarInformationUpdatedSubscription)
    {
      this.calendarInformationUpdatedSubscription.unsubscribe();
    }
    if (this.calendarDataArrivedSubscription)
    {
      this.calendarDataArrivedSubscription.unsubscribe();
    }




  }





}


declare global
{
  interface Window { my: any; }
}
